import type { Dictionary } from 'lodash';
import get from 'lodash/get';
import { UnknownAction, ThunkAction, createAsyncThunk } from '@reduxjs/toolkit';
import { push, replace } from 'redux-first-history';
import type { PartialDeep } from 'type-fest/source/partial-deep';

import {
  Lead,
  LeadInventory,
  LeadPhoto,
  LeadPhotoTag,
  LeadRoom,
} from '@backend/src/frontend-mongo-interfaces/mongoose.gen';
import { getUniqueRoomName } from '@backend/lib/lead/helperFunctions';
import untranslatedRooms from '@backend/lib/mwc/rooms';
import steps from '@backend/lib/lead/steps';
import { photoTagStatus } from '@backend/src/lib/lead/constants';
import { createObjectId, determineCountry } from 'utils';
import logger from 'logger';
import types from 'constants/actionTypes';
import calculateFields from 'lib/calculateFields';
import { generateStorageSpace, isSpaceValid } from 'lib/space';
import { calculateRoomBasedEstimate } from 'lib/calculateRoomBasedEstimate';
import handleFromLocationOrServiceChange from 'lib/handleFromLocationOrServiceChange';
import { setInventorySpaceId } from 'slices/inventorySlice';
import { updateUnsavedLead } from 'slices/unsavedLeadSlice';
// import { leadPhotoTagsBySpaceAndType } from 'lib/photoTag';
import { RootState } from 'store/configureStore';
import { leadGet } from 'services/backend';
import {
  doubleCheckLocationOK,
  processGtmEvents,
  setPercentageComplete,
} from 'actions/formActions';
import {
  calculateFranchiseId,
  getGAValues,
  handleCustomerTypeDidChange,
  getPercentComplete,
} from 'actions/actionUtilities';
import { fireGtmEvent, setUserMessage } from 'actions/appActions';
import backendApi from 'slices/backendApi';
import { verifyCloseLocations } from 'actions/locationActions';
import { calculateMoveDriveDistance } from 'lib/lead';
import { InventoryItemFormSubmitHandlerValues } from 'components/inventory/InventoryItemForm';

const roomDefinitions = untranslatedRooms();

export type AppThunk<ReturnType = Promise<void>> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  UnknownAction
>;

type SimpleInventory = Omit<LeadInventory, '_id'>;

export const saveToStore = (
  detailsPartial: PartialDeep<Lead>,
): UnknownAction => {
  return {
    type: types.SAVE_LEAD_DETAILS,
    detailsPartial,
  };
};

export const saveSpaceToStore = (
  photo: LeadPhoto,
  newInventory: LeadInventory[],
): UnknownAction => {
  return {
    type: types.SAVE_LEAD_SPACE,
    photo,
    newInventory,
  };
};

export const savePhotoToStore = (photo: LeadPhoto): UnknownAction => {
  return {
    type: types.SAVE_LEAD_PHOTO,
    photo,
  };
};

export const apiSubmit = createAsyncThunk(
  'apiSubmit',
  async (lead: PartialDeep<Lead>, { dispatch, getState }) => {
    const state = getState() as RootState;
    const activeLeadID = state.app.activeLeadID; // STATE INJECTION
    const connectionId = state.app.connectionId as string;
    try {
      if (activeLeadID) {
        await dispatch(
          backendApi.endpoints.updateLead.initiate({
            lead: {
              ...lead,
              id: activeLeadID,
            },
            connectionId,
          }),
        ).unwrap();
      } else {
        await dispatch(backendApi.endpoints.createLead.initiate(lead)).unwrap();
      }
    } catch (error) {
      logger.error('apiSubmit error', { error });
      dispatch(replace('/reauth'));
    }
  },
);

export const savePartialLead = createAsyncThunk(
  'savePartialLead',
  async (partialLead: PartialDeep<Lead>, { dispatch, getState }) => {
    dispatch(updateUnsavedLead(partialLead));
    const state = getState() as RootState;
    dispatch(apiSubmit(state.unsavedLead.lead || {}));
    dispatch(saveToStore(partialLead));
  },
);

export const handleWebsocketLeadUpdate = createAsyncThunk(
  'handleWebsocketLeadUpdate',
  async (
    {
      partialLead,
      connectionId,
    }: { partialLead: PartialDeep<Lead>; connectionId?: string },
    { dispatch, getState },
  ) => {
    const state = getState() as RootState;
    const currentConnectionId = state.app.connectionId as string;

    if (connectionId !== currentConnectionId) {
      dispatch(saveToStore(partialLead));
    } else {
      logger.info('handleWebsocketLeadUpdate, skipping same connectionId');
    }
  },
);

export const fetchLead = createAsyncThunk(
  'fetchLead',
  async (_: void, { dispatch, getState }) => {
    const state = getState() as RootState;
    const activeLeadID = state.app.activeLeadID as string;

    try {
      const lead = await leadGet({
        activeLeadID,
      });

      dispatch(saveToStore(lead));

      dispatch(
        verifyCloseLocations(lead.fromAddress) as unknown as UnknownAction,
      );

      return lead;
    } catch (response) {
      logger.error('fetchLead error', response);
      if (`${(response as Dictionary<string>).status}`[0] === '4') {
        dispatch(replace('/reauth'));
      }
    }
  },
  // Not sure if this is needed.
  // {
  //   condition: (_lead, { getState }) => {
  //     const state = getState() as RootState;
  //     if (state.app.backendPending === true) {
  //       return false;
  //     }
  //   },
  // },
);

export const addTag = ({
  photoId,
  Name,
  boundingBox,
}: {
  photoId: LeadPhoto['_id'];
  Name?: LeadPhotoTag['Name'];
  boundingBox: LeadPhotoTag['boundingBox'];
}): AppThunk<Promise<LeadPhotoTag>> => {
  return async (dispatch, getState): Promise<LeadPhotoTag> => {
    const state = getState();
    const lead = state.lead;
    const photo = lead.photos.find((photo) => photo._id === photoId);
    const tags = photo?.tags || [];
    const newTag = {
      boundingBox,
      Name: Name || '',
      _id: createObjectId(),
    };

    dispatch(
      savePartialLead({
        photos: lead.photos.map((photo) => {
          return photo._id === photoId
            ? {
                ...photo,
                tags: [...tags, newTag],
              }
            : photo;
        }),
      }),
    );

    return newTag;
  };
};

export const addSpace = ({
  roomType,
  optionalName = '',
  // hasInventory = true,
  // hasBoxes = true,
}: {
  roomType: LeadRoom['roomType'];
  optionalName?: string;
  hasInventory?: boolean;
  hasBoxes?: boolean;
}): AppThunk<Promise<LeadRoom>> => {
  return async (dispatch, getState): Promise<LeadRoom> => {
    const state = getState();
    const lead = state.lead;
    const _id = createObjectId();
    let rooms = lead.rooms || [];

    // Save a new room.
    const customerType = get(lead, 'customerType', 'home');
    const existingRoomNames = rooms.map((room) => room.optionalName || '');

    const roomTypeLabel = roomDefinitions[customerType][roomType].label;

    const generatedOptionalName = getUniqueRoomName(
      roomTypeLabel,
      existingRoomNames,
    );

    const space: LeadRoom = {
      _id,
      roomType,
      optionalName: optionalName || generatedOptionalName,
      // noInventory: !hasInventory,
      // noBoxes: !hasBoxes,
      isValid: false,
    };

    rooms = [...rooms, space];

    dispatch(savePartialLead({ oneSpace: false, rooms }));

    return space;
  };
};

export type AddSpaceArgs = Parameters<typeof addSpace>[0];

export const addInventory = (
  inventoryItem: SimpleInventory,
): AppThunk<Promise<boolean>> => {
  return async (dispatch, getState): Promise<boolean> => {
    const state = getState();
    const lead = state.lead;
    const { itemType, spaceId, quantity } = inventoryItem;

    const inventory: LeadInventory[] = [
      ...lead.inventory,
      ...Array(quantity).fill({
        itemType,
        spaceId,
        quantity: 1,
        _id: createObjectId(),
      }),
    ];

    dispatch(
      savePartialLead({
        inventory,
        rooms: lead.rooms.map((space) => {
          return `${space._id}` === spaceId
            ? {
                ...space,
                isValid: true,
              }
            : space;
        }),
      }),
    );

    return true;
  };
};

export const updateInventoryItem = ({
  inventoryItem,
  itemType,
}: {
  inventoryItem: LeadInventory;
  itemType: InventoryItemFormSubmitHandlerValues['itemType'];
}): AppThunk<Promise<boolean>> => {
  return async (dispatch, getState): Promise<boolean> => {
    const state = getState();
    const lead = state.lead;

    dispatch(
      savePartialLead({
        inventory: (lead.inventory || []).map((item) => {
          return item._id === inventoryItem._id
            ? {
                ...item,
                itemType,
              }
            : item;
        }),
        photos: lead.photos.map((photo) => {
          return photo._id === inventoryItem.photoId
            ? {
                ...photo,
                tags: photo.tags.map((tag) => {
                  return tag._id === inventoryItem.tagId
                    ? {
                        ...tag,
                        itemType,
                      }
                    : tag;
                }),
              }
            : photo;
        }),
      }),
    );

    return true;
  };
};

export const changeSpaceItemGroupType = ({
  spaceId,
  oldItemType,
  newItemType,
}: {
  spaceId: LeadInventory['spaceId'];
  oldItemType: InventoryItemFormSubmitHandlerValues['itemType'];
  newItemType: InventoryItemFormSubmitHandlerValues['itemType'];
}): AppThunk<Promise<boolean>> => {
  return async (dispatch, getState): Promise<boolean> => {
    const state = getState();
    const lead = state.lead;

    dispatch(
      savePartialLead({
        inventory: (lead.inventory || []).map((item) => {
          return item.spaceId === spaceId && item.itemType === oldItemType
            ? {
                ...item,
                itemType: newItemType,
              }
            : item;
        }),
        photos: lead.photos.map((photo) => {
          return photo.spaceId === spaceId
            ? {
                ...photo,
                tags: photo.tags.map((tag) => {
                  return tag.itemType === oldItemType
                    ? {
                        ...tag,
                        itemType: newItemType,
                      }
                    : tag;
                }),
              }
            : photo;
        }),
      }),
    );

    return true;
  };
};

export const removeSpaceItemGroup = ({
  spaceId,
  itemType,
}: {
  spaceId: LeadInventory['spaceId'];
  itemType: InventoryItemFormSubmitHandlerValues['itemType'];
}): AppThunk<Promise<boolean>> => {
  return async (dispatch, getState): Promise<boolean> => {
    const state = getState();
    const lead = state.lead;

    dispatch(
      savePartialLead({
        inventory: (lead.inventory || []).filter((item) => {
          return !(item.spaceId === spaceId && item.itemType === itemType);
        }),
        photos: lead.photos.map((photo) => {
          return photo.spaceId === spaceId
            ? {
                ...photo,
                tags: photo.tags.filter((tag) => {
                  return tag.itemType !== itemType;
                }),
              }
            : photo;
        }),
      }),
    );

    return true;
  };
};

export const removeInventoryItem = ({
  inventoryItem,
}: {
  inventoryItem: LeadInventory;
}): AppThunk<Promise<boolean>> => {
  return async (dispatch, getState): Promise<boolean> => {
    const state = getState();
    const lead = state.lead;

    if (inventoryItem.tagId) {
      dispatch(
        removePhotoTags({
          tagIds: [inventoryItem.tagId],
          photoId: inventoryItem.photoId || '',
        }),
      );
      return true;
    }

    const inventory = (lead.inventory || []).filter(
      (item) => item._id !== inventoryItem._id,
    );

    const inventoryInSpace = inventory.filter((item) => {
      return item.spaceId === inventoryItem.spaceId;
    });

    dispatch(
      savePartialLead({
        inventory,
        rooms: lead.rooms.map((space) => {
          return `${space._id}` === inventoryItem.spaceId
            ? {
                ...space,
                isValid: isSpaceValid({
                  inventoryCount: inventoryInSpace.length,
                }),
              }
            : space;
        }),
      }),
    );

    return true;
  };
};

export const saveStep = createAsyncThunk(
  'saveStep',
  async (
    {
      stepToSave,
      lead,
      stepToNagivateTo = '',
      pushHistory = true,
    }: {
      stepToSave: string;
      stepToNagivateTo: string | number;
      pushHistory?: boolean;
      lead?: PartialDeep<Lead>;
    },
    { dispatch, getState },
  ) => {
    const step = steps()[stepToSave];
    const state = getState() as RootState;
    const existingLead = state.lead;
    const currentLocation = get(state, 'app.currentLocation'); // STATE INJECTION

    dispatch(setUserMessage(null)); // Clear the message after every step

    const gaValues = getGAValues();

    // This only contains the updated values.
    let incomingLead = {
      email: existingLead.email,
      phone: existingLead.phone,
      locationID: existingLead.locationID,
      ...(lead || {}),
      ...gaValues,
      domainCountry: determineCountry(),
    } as PartialDeep<Lead>;

    // This contains the entire existing lead with all the updated fields as well.
    // Yes, this isn't immutable. But it's much easier to read.
    let updatedLead = { ...existingLead, ...incomingLead } as PartialDeep<Lead>;

    incomingLead.moveDriveDistance = updatedLead.moveDriveDistance =
      await calculateMoveDriveDistance({
        incomingLead,
        existingLead,
        updatedLead,
      });

    incomingLead.franchiseID = updatedLead.franchiseID = calculateFranchiseId({
      updatedLead,
    });

    incomingLead = handleFromLocationOrServiceChange({
      incomingLead,
      existingLead,
      stepToSave,
      updatedLead,
    });

    updatedLead = { ...updatedLead, ...incomingLead };

    incomingLead = {
      ...incomingLead,
      ...handleCustomerTypeDidChange({
        existingLead,
        updatedLead,
      }),
    };
    updatedLead = { ...updatedLead, ...incomingLead };

    incomingLead.roomBasedEstimate = updatedLead.roomBasedEstimate =
      calculateRoomBasedEstimate({
        currentLocation,
        updatedLead,
      });

    incomingLead.locationOK = updatedLead.locationOK = doubleCheckLocationOK({
      updatedLead,
    });

    incomingLead = {
      ...incomingLead,
      ...calculateFields({
        incomingLead,
        stepToSave,
        stepToNagivateTo,
        updatedLead,
      }),
    };

    updatedLead = { ...updatedLead, ...incomingLead };

    if (step.canBeFinal === true) {
      // Submit the entire lead to the backend if this is the end of the form.
      await dispatch(savePartialLead(updatedLead));
    } else {
      dispatch(savePartialLead(incomingLead));
    }

    dispatch(
      processGtmEvents({
        stepToSave,
        updatedLead,
        existingLead,
        stepToNagivateTo: `${stepToNagivateTo}`,
      }),
    );
    if (stepToNagivateTo) {
      dispatch(
        setPercentageComplete(
          getPercentComplete(incomingLead.currentStep, incomingLead),
        ),
      );
      if (incomingLead.formComplete) {
        if (incomingLead.roomsStageComplete) {
          dispatch(fireGtmEvent('roomsComplete'));
        }
        if (incomingLead.inventoryStageComplete) {
          dispatch(fireGtmEvent('inventoryComplete'));
        }
      } else {
        if (pushHistory) {
          dispatch(push(incomingLead.currentStep as string));
        } else {
          dispatch(replace(incomingLead.currentStep as string));
        }
      }
    }
    // };
  },
);

export const setSkipSpaces = (): AppThunk => {
  return async (dispatch) => {
    const storageRoom = generateStorageSpace();
    dispatch(
      savePartialLead({
        oneSpace: true,
        rooms: [storageRoom],
      }),
    );

    dispatch(setInventorySpaceId(`${storageRoom._id}`));
  };
};

export const setSpaceNoBoxes = createAsyncThunk(
  'setSpaceNoBoxes',
  async (
    {
      spaceId,
      // noBoxes,
    }: {
      spaceId: LeadRoom['_id'];
      noBoxes: LeadRoom['noBoxes'];
    },
    { dispatch, getState },
  ) => {
    const state = getState() as RootState;
    const lead = state.lead;

    // const boxesInSpace = lead.boxes.filter((item) => {
    //   return item.spaceId === `${spaceId}`;
    // });

    const inventoryInSpace = lead.inventory.filter((item) => {
      return item.spaceId === `${spaceId}`;
    });

    return dispatch(
      savePartialLead({
        rooms: lead.rooms.map((space) => {
          return `${space._id}` === `${spaceId}`
            ? {
                ...space,
                // noBoxes,
                isValid: isSpaceValid({
                  // space: {
                  //   ...space,
                  //   noBoxes,
                  // },
                  // inventory,
                  inventoryCount: inventoryInSpace.length,
                  // boxes: lead.boxes,
                  // boxCount: boxesInSpace.length,
                }),
              }
            : space;
        }),
      }),
    );
  },
);

export const removeSpace = (spaceId: LeadInventory['_id']): AppThunk => {
  return async (dispatch, getState) => {
    const state = getState();
    const lead = state.lead;

    dispatch(
      savePartialLead({
        rooms: (lead.rooms || []).filter((room) => {
          return room._id !== spaceId;
        }),
        boxes: (lead.boxes || []).filter((item) => {
          return item.spaceId !== `${spaceId}`;
        }),
        inventory: (lead.inventory || []).filter((item) => {
          return item.spaceId !== `${spaceId}`;
        }),
        photos: (lead.photos || []).filter((photo) => {
          return photo.spaceId !== `${spaceId}`;
        }),
      }),
    );
  };
};

export const removePhoto = (photoId: LeadPhoto['_id']): AppThunk => {
  return async (dispatch, getState) => {
    const state = getState();
    const lead = state.lead;
    const photo = lead.photos.find(
      (photo) => photo._id === photoId,
    ) as LeadPhoto;
    const spaceId = photo.spaceId;

    const inventory = lead.inventory.filter((item) => item.photoId !== photoId);

    const inventoryInSpace = inventory.filter((item) => {
      return item.spaceId === spaceId;
    });

    dispatch(
      savePartialLead({
        inventory,
        photos: (lead.photos || []).filter((photo) => {
          return photo._id !== photoId;
        }),
        rooms: lead.rooms.map((space) => {
          return `${space._id}` === `${spaceId}`
            ? {
                ...space,
                isValid: isSpaceValid({
                  // space,
                  // inventory,
                  inventoryCount: inventoryInSpace.length,
                  // boxes: lead.boxes,
                  // boxCount: boxesInSpace.length,
                }),
              }
            : space;
        }),
      }),
    );
  };
};

export const removePhotoTags = createAsyncThunk(
  'removePhotoTags',
  async (
    {
      tagIds,
      photoId,
    }: {
      tagIds: LeadPhotoTag['_id'][];
      photoId: LeadPhoto['_id'];
    },
    { dispatch, getState },
  ) => {
    logger.info('removePhotoTags', { tagIds, photoId });
    const state = getState() as RootState;
    const lead = state.lead;

    const photo = lead.photos.find(
      (photo) => photo._id === photoId,
    ) as LeadPhoto;

    const { spaceId } = photo;

    const tags = photo.tags.filter((tag) => !tagIds.includes(tag._id));

    const inventory = lead.inventory.filter(
      (item) => !tagIds.includes(item.tagId || ''),
    );

    const inventoryInSpace = inventory.filter((item) => {
      return item.spaceId === spaceId;
    });

    return dispatch(
      savePartialLead({
        photos: [
          ...lead.photos.map((photo) => {
            return photo._id !== photoId
              ? photo
              : {
                  ...photo,
                  tags,
                };
          }),
        ],
        inventory,
        rooms: lead.rooms.map((space) => {
          return `${space._id}` === spaceId
            ? {
                ...space,
                isValid: isSpaceValid({
                  // space,
                  // inventory,
                  inventoryCount: inventoryInSpace.length,
                  // boxes: lead.boxes,
                  // boxCount: boxesInSpace.length,
                }),
              }
            : space;
        }),
      }),
    );
  },
);

export type AddPhotoTagAndInventoryArgs = {
  photoId: LeadPhoto['_id'];
  spaceId: LeadInventory['spaceId'];
  items: {
    itemType: LeadInventory['itemType'];
    quantity: NonNullable<LeadPhotoTag['quantity']>;
    boundingBox: LeadPhotoTag['boundingBox'];
  }[];
};

export const addMultiplePhotoTagsAndInventory = createAsyncThunk(
  'addMultiplePhotoTagsAndInventory',
  async (
    { photoId, spaceId, items }: AddPhotoTagAndInventoryArgs,
    { dispatch, getState },
  ) => {
    logger.info('addPhotoTagAndInventory', { photoId, items });
    const state = getState() as RootState;
    const lead = state.lead;

    const newTags: LeadPhotoTag[] = items.map((item) => ({
      _id: createObjectId(),
      itemType: item.itemType,
      quantity: parseInt(`${item.quantity}`, 10),
      boundingBox: item.boundingBox,
    }));

    const photos: LeadPhoto[] = (lead.photos || []).map((photo) => {
      return photo._id !== photoId
        ? photo
        : {
            ...photo,
            // A photo is considered reviewed when there's at least one tagged label.
            // status: photoStatus.Reviewed,
            tags: [...(photo.tags || []), ...newTags],
          };
    });

    const inventory: LeadInventory[] = [
      ...lead.inventory,
      ...newTags.map((item) => ({
        _id: item._id,
        tagId: item._id,
        photoId,
        itemType: item.itemType || 0,
        spaceId,
        quantity: parseInt(`${item.quantity}`, 10),
      })),
    ];

    return dispatch(
      savePartialLead({
        photos,
        inventory,
        rooms: lead.rooms.map((space) => {
          return `${space._id}` === spaceId
            ? {
                ...space,
                isValid: true,
              }
            : space;
        }),
      }),
    );
  },
);

export const addInventoryToPhoto = createAsyncThunk(
  'addInventoryToPhoto',
  async (
    {
      photoId,
      spaceId,
      items,
    }: {
      photoId: LeadPhoto['_id'];
      spaceId: LeadInventory['spaceId'];
      items: {
        itemType: LeadInventory['itemType'];
        tagId: LeadPhotoTag['_id'];
        quantity: NonNullable<LeadPhotoTag['quantity']>;
        boundingBox?: LeadPhotoTag['boundingBox'];
      }[];
    },
    { dispatch, getState },
  ) => {
    logger.info('addInventoryToPhoto', { photoId, items });
    const state = getState() as RootState;
    const lead = state.lead;

    const keyedUpdatedTags = Object.fromEntries(
      items.map(({ tagId, quantity, itemType, boundingBox }) => [
        tagId,
        {
          quantity,
          itemType,
          ...(boundingBox ? { boundingBox } : {}),
        },
      ]),
    );

    const updatedTagIds = Object.keys(keyedUpdatedTags);

    const photos: LeadPhoto[] = (lead.photos || []).map((photo) => {
      return photo._id !== photoId
        ? photo
        : {
            ...photo,
            // A photo is considered reviewed when there's at least one tagged label.
            // status: photoStatus.Reviewed,
            tags: photo.tags.map((tag) => {
              return !updatedTagIds.includes(tag._id)
                ? tag
                : {
                    ...tag,
                    status: photoTagStatus.Reviewed,
                    ...keyedUpdatedTags[tag._id],
                  };
            }),
          };
    });

    const inventory: LeadInventory[] = [
      ...lead.inventory,
      ...items.map(({ tagId, itemType, quantity }) => ({
        _id: tagId,
        tagId,
        photoId,
        itemType,
        spaceId,
        quantity: parseInt(`${quantity}`, 10),
      })),
    ];

    return dispatch(
      savePartialLead({
        photos,
        inventory,
        rooms: lead.rooms.map((space) => {
          return `${space._id}` === spaceId
            ? {
                ...space,
                isValid: true,
              }
            : space;
        }),
      }),
    );
  },
);

export const updatePhotoTagTmpStatuses = createAsyncThunk(
  'updatePhotoTagTmpStatuses',
  async (
    {
      photoId,
      tagIds,
      tmpStatus,
    }: {
      photoId: LeadPhoto['_id'];
      tagIds: LeadPhotoTag['_id'][];
      tmpStatus: LeadPhotoTag['tmpStatus'];
    },
    { dispatch, getState },
  ) => {
    logger.info('updatePhotoTagTmpStatuses', { tagIds, tmpStatus });
    const state = getState() as RootState;
    const lead = state.lead;

    const photos: LeadPhoto[] = (lead.photos || []).map((photo) => {
      return photo._id !== photoId
        ? photo
        : {
            ...photo,
            tags: photo.tags.map((tag) => {
              return tagIds.includes(tag._id) === false
                ? tag
                : { ...tag, tmpStatus };
            }),
          };
    });

    return dispatch(
      saveToStore({
        photos,
      }),
    );
  },
);

export const savePhotoTagTmpStatuses = createAsyncThunk(
  'savePhotoTagTmpStatuses',
  async (
    {
      photoId,
    }: {
      photoId: LeadPhoto['_id'];
    },
    { dispatch, getState },
  ) => {
    logger.info('savePhotoTagTmpStatuses', { photoId });
    const state = getState() as RootState;
    const lead = state.lead;

    const photos: LeadPhoto[] = (lead.photos || []).map((photo) => {
      return photo._id !== photoId
        ? photo
        : {
            ...photo,
            tags: photo.tags.map((tag) => ({
              ...tag,
              status: tag.tmpStatus,
            })),
          };
    });

    return dispatch(
      savePartialLead({
        photos,
      }),
    );
  },
);

export const savePhotoTagStatus = createAsyncThunk(
  'savePhotoTagStatus',
  async (
    {
      photoId,
      tagId,
      status,
    }: {
      photoId: LeadPhoto['_id'];
      tagId: LeadPhotoTag['_id'];
      status: LeadPhotoTag['status'];
    },
    { dispatch, getState },
  ) => {
    logger.info('savePhotoTagStatus', { photoId, tagId, status });
    const state = getState() as RootState;
    const lead = state.lead;

    const photos: LeadPhoto[] = (lead.photos || []).map((photo) => {
      return photo._id !== photoId
        ? photo
        : {
            ...photo,
            tags: photo.tags.map((tag) => {
              return tag._id !== tagId
                ? tag
                : {
                    ...tag,
                    status,
                  };
            }),
          };
    });

    return dispatch(
      savePartialLead({
        photos,
      }),
    );
  },
);

export const handleBrowserNavigation = ({
  stepToSave,
  stepToNagivateTo = '',
}: {
  stepToSave: string;
  stepToNagivateTo: string | number;
}): AppThunk => {
  return async (dispatch, getState) => {
    const state = getState();
    const existingLead = state.lead;
    const updatedLead = {
      existingLead,
      currentStep: stepToNagivateTo,
    };

    dispatch(setUserMessage(null)); // Clear the message after every step

    dispatch(
      savePartialLead({
        currentStep: `${stepToNagivateTo}`,
      }),
    );

    dispatch(processGtmEvents({ stepToSave, updatedLead, existingLead }));
    dispatch(
      setPercentageComplete(
        getPercentComplete(updatedLead.currentStep, updatedLead),
      ),
    );
  };
};
